/**
 * MUI Overlaps component that replaces the styled-components Overlaps component
 * 
 * This component provides a Material UI equivalent of the original Overlaps component
 * with similar props and functionality.
 */
import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ReducersIndex from "reduxModules/ducks";
import { parseDistance } from "utilities/format";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { values } from "lodash";
import { 
  MuiOverlapsProps, 
  AreaData, 
  OverlapBox 
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI Overlaps component
 */
const MuiOverlaps: React.FC<MuiOverlapsProps> = (props) => {
  const areasArray = values(useSelector((state: RootState) => state.container.areas));
  const areas: AreaData[] = areasArray.map(area => ({ 
    name: area.name, 
    ...area.location, 
    ...area.dimensions 
  }));
  
  const { length, width } = useSelector((state: RootState) => state.container.dimensions);

  const { bbox, scale, svgbox } = useSelector(getDrawingBox);

  const currentOverlaps = useSelector((state: RootState) => state.ui.overlaping);

  const overlaps: OverlapBox[] = [];
  const overlaping: string[] = []; 

  areas.forEach((r1, i) => {
    areas.slice(i + 1).forEach((r2) => {
      const ck1 = parseDistance(r2.left) >= parseDistance(r1.left + r1.length);
      const ck2 = parseDistance(r2.left + r2.length) <= parseDistance(r1.left);
      const ck3 = parseDistance(r2.top) >= parseDistance(r1.top + r1.width);
      const ck4 = parseDistance(r2.top + r2.width) <= parseDistance(r1.top);
      
      if (!(ck1 || ck2 || ck3 || ck4)) {
          overlaping.push(`${r1.name} is overlaping ${r2.name}`);
          const x = Math.max(r1.left, r2.left) * scale;
          const y = Math.max(r1.top, r2.top) * scale;
          const h = Math.min(r1.top+r1.width, r2.top+r2.width) * scale - y;
          const w = Math.min(r1.left+r1.length, r2.left+r2.length) * scale - x;
          overlaps.push({ x: x + bbox.x0, y: y + bbox.y0, h: h, w: w });
      }
    });
    
    const ck5 = parseDistance(r1.left + r1.length) > length;
    const ck6 = parseDistance(r1.top + r1.width) > width;
    
    if (ck5 || ck6) {
      overlaping.push(`${r1.name} is outside the Warehouse`);
      const x = r1.left * scale;
      const y = r1.top * scale;
      const h = (r1.top + r1.width) * scale - y;
      const w = (r1.left + r1.length) * scale - x;
      
      if (ck6) {
        overlaps.push({ 
          x: x + bbox.x0, 
          y: bbox.y1, 
          h: svgbox.height - bbox.height - bbox.y0, 
          w: w 
        });
      }
      
      if (ck5) {
        overlaps.push({ 
          x: bbox.x1, 
          y: y + bbox.y0, 
          h: h, 
          w: svgbox.width - bbox.width - bbox.x0 
        });
      }
    }
  });

  const dispatch = useDispatch();
  
  useEffect(() => {
    if(JSON.stringify(currentOverlaps) !== JSON.stringify(overlaping)) {
      dispatch(ReducersIndex.uiReducers.setOverlaping(overlaping));
    }
  });

  const visible = useSelector((state: RootState) => getConditionResult(state, {...props.show}));

  return (
    visible && (overlaps.length > 0) && (
      <OverlapsGroup id="overlaps">
        {overlaps.map((box, index) => (
          <OverlapsRect 
            key={`ov-${index}`} 
            width={Math.max(8, box.w)}
            height={Math.max(8, box.h)}
            x={box.x - (box.w <= 6 ? 3 : 0)}
            y={box.y - (box.h <= 6 ? 3 : 0)}
            rx={3}
          />
        ))}
      </OverlapsGroup>
    )
  );
};

// Styled components using MUI styled API
const OverlapsGroup = styled('g')({
  fill: '#007bff',
  opacity: 0.4,
});

const OverlapsRect = styled('rect')({
  // No additional styling needed as we're using inline props
});

export default MuiOverlaps;
